import {
   ChangeDetectorRef,
   Component,
   Input,
   NgZone,
   OnChanges,
   OnDestroy,
   OnInit,
   SimpleChanges,
   ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CallState, LoadingState } from '@lcs/shared/api';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { OrderingDirections, QueueCallerStatus } from 'generated/graphql-types';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { getCurrentUserIsSystemUser, getIsLoggedIn } from 'src/app/authentication/state/authentication.selector';
import { Register } from 'src/app/common-components/register/models/register';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { getHangUpCallsPrivileges } from 'src/app/privileges/privileges.selector';
import {
   extensionCallQueueCallsChanged,
   extensionCallQueueCompleted,
   extensionCallQueueIsLoggedInChanged,
   extensionCallQueueStatusChanged,
} from 'src/app/signalr/extension-call-queue-hub/extension-call-queue-hub.actions';
import { ExtensionCallQueueHubService } from 'src/app/signalr/extension-call-queue-hub/extension-call-queue-hub.service';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { saveQueueHistoryRegisterWrapQueues } from 'src/app/store/user-preferences/user-preferences.actions';
import { getQueueHistoryRegisterWrapQueuesUserPreference } from 'src/app/store/user-preferences/user-preferences.selectors';
import { HangUpCallsPrivileges } from '../../active-calls/models/hang-up-calls.privileges';
import { ExtensionOption } from '../../extension-register/models/extension-option.interface';
import { QueueHistoryFilterOptions } from '../models/queue-history-filter-option';
import { QueueHistoryOrderingOptions, QueueHistoryRegisterOptions } from '../models/queue-history-register-options';
import {
   LoggedInQueueCallDisplay,
   LoggedInQueueDisplay,
   LoggedInQueueMemberDisplay,
   LoggedInQueueMemberStatus,
   LoggedInQueueReference,
} from '../models/queue-history-register-queue-members.model';
import { QueueHistoryRegisterListItem } from '../models/queue-history-register-results.model';
import { GetQueueHistory_queueHistoriesConnection_items } from '../queries/generated/GetQueueHistory';
import { QueueHistoryRegisterApiService } from '../services/queue-history-register-api.service';
import {
   QueueHistoryRegisterSessionStorageService,
   QueueHistoryToggleState,
} from '../services/queue-history-register-session-storage.service';
import * as queueHistoryActions from '../state/queue-history-register.actions';
import {
   clearQueueCall,
   loadAccountQueueMembersSuccess,
   queueHistoryRegisterAdapterActions,
   queueHistoryRegisterInitialFilter,
   sendToEmptyExitRoute,
} from '../state/queue-history-register.actions';
import * as queueHistoryRegisterReducer from '../state/queue-history-register.reducer';

@Component({
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-queue-history-register',
   styleUrls: ['./queue-history-register.component.scss'],
   templateUrl: './queue-history-register.component.html',
   standalone: false,
})
export class QueueHistoryRegisterComponent
   extends Register<QueueHistoryRegisterListItem, QueueHistoryRegisterOptions, QueueHistoryOrderingOptions>
   implements OnInit, OnDestroy, OnChanges
{
   @Input() extensionId: number | null = null;

   loadSuccess$: Observable<unknown>;

   isAccountWide = false;

   queueExtensions: ExtensionOption[] = [];

   selectedQueueExtensionIds: number[] = [];

   loggedInQueueMembers: LoggedInQueueMemberDisplay[] = [];

   loggedInQueueGroups: LoggedInQueueDisplay[] = [];

   queueCallGroups: LoggedInQueueCallDisplay[] = [];

   hangupActiveCallsPrivileges$: Observable<HangUpCallsPrivileges>;

   groupMembersByQueue = false;

   wrapQueueSwimlanes = true;

   queueMembersGridRefreshVersion = 0;

   selectedAccountWideTabIndex = 0;

   queueMembersCallState: CallState = LoadingState.Loading;

   isSystemUser$: Observable<boolean>;

   readonly showAgentStatusTab = true;

   readonly showQueueStatusTab = true;

   readonly showQueueCallsTab = true;

   readonly queueCallsTabIndex = 0;

   readonly queueStatusTabIndex = 2;

   readonly agentStatusTabIndex = 3;

   columns = [
      'callerIdName',
      'callerIdNumber',
      'callerStatus',
      'agentExtension',
      'enqueuedDateTime',
      'isCallbackRequested',
      'callbackAttempts',
      'waitTime',
      'queueHistoryActionsMenu',
   ];

   filters: QueueHistoryFilterOptions[] = [];

   entityName = 'Queue History';

   private readonly extensionColumns = [
      'callerIdName',
      'callerIdNumber',
      'callerStatus',
      'agentExtension',
      'enqueuedDateTime',
      'isCallbackRequested',
      'callbackAttempts',
      'waitTime',
      'queueHistoryActionsMenu',
   ];

   private readonly accountWideColumns = [
      'queueExtension',
      'callerIdName',
      'callerIdNumber',
      'callerStatus',
      'agentExtension',
      'order',
      'isCallbackRequested',
      'callbackAttempts',
      'waitTime',
      'queueHistoryActionsMenu',
   ];

   private readonly extensionOrderingOptions: QueueHistoryOrderingOptions[] = [
      { field: 'enqueuedDateTime', direction: OrderingDirections.Asc },
   ];

   private readonly accountWideOrderingOptions: QueueHistoryOrderingOptions[] = [
      { field: 'enqueuedDateTime', direction: OrderingDirections.Asc },
   ];

   selectedAccountId: number | null = null;

   private accountQueueRefreshTimer: ReturnType<typeof setInterval> | null = null;

   private allAccountQueueMembers: LoggedInQueueMemberStatus[] = [];

   private accountWideQueueHistoryRows: QueueHistoryRegisterListItem[] = [];

   private hasInitializedQueueExtensionSelection = false;

   private queueHistorySnapshotRequestId = 0;

   private nextQueueMembersLoadingToken = 0;

   private activeQueueMembersLoadingTokens = new Set<number>();

   private pendingAccountQueueLoadToken: number | null = null;

   private onPhoneQueueByAgentId = new Map<number, Set<number>>();
   private ringingQueueByAgentId = new Map<number, Set<number>>();
   private activeQueueCallByAgentId = new Map<number, QueueHistoryRegisterListItem>();
   private accountSelectionSub: Subscription | null = null;
   private isDestroyed = false;

   private readonly accountWideTabRouteNames = ['queuecalls', 'activecalls', 'queueagents', 'agentstatus'];

   constructor(
      public store: Store,
      private route: ActivatedRoute,
      private extensionCallQueueHubService: ExtensionCallQueueHubService,
      private actionsSubject: ActionsSubject,
      private ngZone: NgZone,
      private cdRef: ChangeDetectorRef,
      private queueHistoryRegisterApiService: QueueHistoryRegisterApiService,
      private sessionStorageService: QueueHistoryRegisterSessionStorageService
   ) {
      super(store, queueHistoryRegisterReducer.selectors, queueHistoryRegisterAdapterActions);
      this.isSystemUser$ = this.store.pipe(select(getCurrentUserIsSystemUser));
      this.loadSuccess$ = this.actionsSubject.pipe(
         filter((action) => action.type === queueHistoryRegisterAdapterActions.loadSuccess.type),
         take(1)
      );
      this.hangupActiveCallsPrivileges$ = store.pipe(select(getHangUpCallsPrivileges));
   }

   ngOnInit() {
      this.subscribeToHubReconnects();
      this.subscribeToQueueMemberEvents();
      this.subscribeToAccountQueueMemberLoads();
      this.subscribeToWrapQueuesPreference();

      if (this.extensionId) {
         this.initializeExtensionMode();
         return;
      }

      this.initializeAccountWideMode();
   }

   ngOnChanges(changes: SimpleChanges) {
      if (changes['extensionId'] && !changes['extensionId'].firstChange) {
         if (this.extensionId) {
            this.stopAccountQueueRefresh();
            this.initializeExtensionMode();
            return;
         }

         this.initializeAccountWideMode();
      }
   }

   override ngOnDestroy(): void {
      this.isDestroyed = true;
      this.queueHistorySnapshotRequestId++;
      this.accountSelectionSub?.unsubscribe();
      this.extensionCallQueueHubService.unregisterExtensionQueueIsLoggedInEvents(this.selectedAccountId);
      this.stopAccountQueueRefresh();
      this.resetQueueMembersLoadingState();
      super.ngOnDestroy();
   }

   onClearCall(queueHistory: QueueHistoryRegisterListItem) {
      this.store.dispatch(clearQueueCall({ queueHistory }));
   }
   onOpenCallDetails(queueHistory: QueueHistoryRegisterListItem) {
      if (
         (!queueHistory.isCallbackRequested && queueHistory.callerStatus !== QueueCallerStatus.InitiateQueueCallback) ||
         queueHistory.callerStatus === QueueCallerStatus.Waiting ||
         queueHistory.callerStatus === QueueCallerStatus.DialingAnAgent ||
         queueHistory.callerStatus === QueueCallerStatus.Connected
      )
         this.store.dispatch(queueHistoryActions.openCallDetailsForActive({ queueHistory }));
      else this.store.dispatch(queueHistoryActions.openCallDetailsForCallDetail({ queueHistory }));
   }

   onEmptyExitRoute(queueHistory: QueueHistoryRegisterListItem) {
      this.store.dispatch(sendToEmptyExitRoute({ queueHistory }));
   }

   onQueueExtensionIdsChange(queueExtensionIds: number[]): void {
      if (!this.isAccountWide) {
         return;
      }

      this.selectedQueueExtensionIds = [...(queueExtensionIds ?? [])];
      this.hasInitializedQueueExtensionSelection = true;
      this.sessionStorageService.setSelectedQueueExtensionIds(this.selectedQueueExtensionIds);
      this.filters = this.buildFilters();
      this.dispatchInitialFilters(undefined, null);
      this.updateLoggedInQueueMembers();
      void this.refreshAccountWideQueueHistorySnapshot(this.startQueueMembersLoading());
   }

   onGroupMembersByQueueChange(isGroupedByQueue: boolean): void {
      this.groupMembersByQueue = isGroupedByQueue;
      this.saveQueueHistoryToggleState({ groupMembersByQueue: isGroupedByQueue });
   }

   onWrapSwimlanesChange(wrapSwimlanes: boolean): void {
      this.wrapQueueSwimlanes = wrapSwimlanes;
      this.saveQueueHistoryToggleState({
         queuesWrapSwimlanes: wrapSwimlanes,
         queueCallsWrapSwimlanes: wrapSwimlanes,
      });
      this.store.dispatch(saveQueueHistoryRegisterWrapQueues({ queueHistoryRegisterWrapQueues: wrapSwimlanes }));
   }

   onSelectedAccountWideTabIndexChange(tabIndex: number): void {
      this.selectedAccountWideTabIndex = tabIndex;

      if (this.isQueueMembersTabIndex(tabIndex)) {
         return;
      }

      this.triggerActiveQueueCallsTableReflow();
   }

   onAccountWideTabAnimationDone(): void {
      if (this.isQueueMembersTabIndex(this.selectedAccountWideTabIndex)) {
         this.queueMembersGridRefreshVersion++;
         return;
      }

      this.triggerActiveQueueCallsTableReflow();
   }

   private initializeExtensionMode(): void {
      this.resetQueueMembersLoadingState();
      this.accountSelectionSub?.unsubscribe();
      this.accountSelectionSub = null;
      this.isAccountWide = false;
      this.columns = this.extensionColumns;
      this.queueHistorySnapshotRequestId++;
      this.accountWideQueueHistoryRows = [];
      this.queueCallGroups = [];
      this.resetOnPhoneQueueMaps();
      this.store
         .pipe(
            select(getSelectedAccountId),
            filter((accountId: number | null): accountId is number => accountId !== null),
            take(1)
         )
         .subscribe((accountId) => {
            this.selectedAccountId = accountId;
            this.extensionCallQueueHubService.registerExtensionQueueIsLoggedInEvents(accountId);
         });
      this.filters = this.buildFilters();
      this.dispatchInitialFilters(this.extensionOrderingOptions, this.extensionId);
   }

   private initializeAccountWideMode(): void {
      this.isAccountWide = true;
      this.queueMembersCallState = LoadingState.Loading;
      this.columns = this.accountWideColumns;
      this.selectedAccountWideTabIndex = this.getSelectedAccountWideTabIndexFromRoute();
      this.restoreQueueHistoryToggleState();

      this.accountSelectionSub?.unsubscribe();
      this.accountSelectionSub = this.store
         .pipe(
            select(getSelectedAccountId),
            filter((accountId: number | null): accountId is number => accountId !== null)
         )
         .subscribe((accountId) => {
            if (this.selectedAccountId === accountId && this.queueExtensions.length > 0) {
               this.resetQueueMembersLoadingState();
               return;
            }

            this.selectedAccountId = accountId;
            this.pendingAccountQueueLoadToken = this.startQueueMembersLoading();
            this.resetOnPhoneQueueMaps();
            this.resetAccountWideQueueViewData();
            this.restoreSelectedQueueExtensionIds();
            this.extensionCallQueueHubService.registerExtensionQueueIsLoggedInEvents(accountId);
            this.loadAccountQueueData(true);
            this.startAccountQueueRefresh();
         });
   }

   private loadAccountQueueData(reloadQueueHistoryRegister: boolean): void {
      if (!this.selectedAccountId) {
         return;
      }

      this.store.dispatch(
         queueHistoryActions.loadAccountQueueMembers({
            accountId: this.selectedAccountId,
            reloadQueueHistoryRegister,
         })
      );
   }

   private subscribeToAccountQueueMemberLoads(): void {
      this.actionsSubject
         .pipe(
            filter((action): action is ReturnType<typeof loadAccountQueueMembersSuccess> => {
               return action.type === loadAccountQueueMembersSuccess.type;
            }),
            takeUntil(this.destroyed$)
         )
         .subscribe((action) => {
            if (!this.isAccountWide) {
               return;
            }

            this.applyAccountQueueMemberData(
               action.queueExtensions,
               action.allAccountQueueMembers,
               action.reloadQueueHistoryRegister ? this.pendingAccountQueueLoadToken : null
            );

            if (action.reloadQueueHistoryRegister) {
               this.filters = this.buildFilters();
               this.dispatchInitialFilters(this.accountWideOrderingOptions, null);
            }
         });

      this.actionsSubject
         .pipe(
            filter((action) => {
               return action.type === queueHistoryActions.loadAccountQueueMembersFailure.type;
            }),
            takeUntil(this.destroyed$)
         )
         .subscribe(() => {
            if (this.pendingAccountQueueLoadToken == null) {
               return;
            }

            this.selectedQueueExtensionIds = [];
            this.resetAccountWideQueueViewData();
            this.finishQueueMembersLoading(this.pendingAccountQueueLoadToken);
            this.pendingAccountQueueLoadToken = null;
         });
   }

   private applyAccountQueueMemberData(
      queueExtensions: ExtensionOption[],
      allAccountQueueMembers: LoggedInQueueMemberStatus[],
      loadingToken: number | null = null
   ): void {
      const selectedQueueIds = this.selectedQueueExtensionIds;
      const previousQueueIds = this.queueExtensions.map((queueExtension) => queueExtension.extensionId);
      const nextQueueIds = (queueExtensions ?? []).map((queueExtension) => queueExtension.extensionId);
      const hadAllQueuesSelected =
         previousQueueIds.length > 0 &&
         selectedQueueIds.length === previousQueueIds.length &&
         previousQueueIds.every((queueExtensionId) => selectedQueueIds.includes(queueExtensionId));

      this.queueExtensions = queueExtensions ?? [];
      this.allAccountQueueMembers = allAccountQueueMembers ?? [];

      const availableQueueIds = new Set(nextQueueIds);
      const selectedAvailableQueueIds = selectedQueueIds.filter((queueExtensionId) => {
         return availableQueueIds.has(queueExtensionId);
      });

      if (!this.hasInitializedQueueExtensionSelection) {
         this.selectedQueueExtensionIds = nextQueueIds;
      } else if (hadAllQueuesSelected) {
         const selectedAvailableQueueIdsSet = new Set(selectedAvailableQueueIds);
         const newQueueIds = nextQueueIds.filter((queueExtensionId) => {
            return !selectedAvailableQueueIdsSet.has(queueExtensionId);
         });
         this.selectedQueueExtensionIds = [...selectedAvailableQueueIds, ...newQueueIds];
      } else {
         this.selectedQueueExtensionIds = selectedAvailableQueueIds;
      }
      this.hasInitializedQueueExtensionSelection = true;

      this.updateLoggedInQueueMembers();
      void this.refreshAccountWideQueueHistorySnapshot(loadingToken);
   }

   private restoreSelectedQueueExtensionIds(): void {
      const savedQueueExtensionIds = this.sessionStorageService.getSelectedQueueExtensionIds();

      this.selectedQueueExtensionIds = savedQueueExtensionIds ?? [];
      this.hasInitializedQueueExtensionSelection = savedQueueExtensionIds !== null;
   }

   private restoreQueueHistoryToggleState(): void {
      const savedToggleState = {
         ...this.getDefaultQueueHistoryToggleState(),
         ...(this.sessionStorageService.getQueueHistoryToggleState() ?? {}),
      };

      this.groupMembersByQueue = savedToggleState.groupMembersByQueue;
      this.wrapQueueSwimlanes = savedToggleState.queuesWrapSwimlanes && savedToggleState.queueCallsWrapSwimlanes;
      this.saveQueueHistoryToggleState({
         queuesWrapSwimlanes: this.wrapQueueSwimlanes,
         queueCallsWrapSwimlanes: this.wrapQueueSwimlanes,
      });
   }

   private saveQueueHistoryToggleState(toggleStatePatch: Partial<QueueHistoryToggleState>): void {
      const existingToggleState = this.sessionStorageService.getQueueHistoryToggleState() ?? {};

      this.sessionStorageService.setQueueHistoryToggleState({
         ...this.getDefaultQueueHistoryToggleState(),
         ...existingToggleState,
         ...toggleStatePatch,
      });
   }

   private getDefaultQueueHistoryToggleState(): QueueHistoryToggleState {
      return {
         groupMembersByQueue: false,
         agentsShowLoggedOutAgents: false,
         queuesShowLoggedOutAgents: false,
         queuesWrapSwimlanes: true,
         queueCallsIncludeActiveCalls: false,
         queueCallsShowEmptyQueues: false,
         queueCallsWrapSwimlanes: true,
      };
   }

   private subscribeToWrapQueuesPreference(): void {
      this.store
         .pipe(select(getQueueHistoryRegisterWrapQueuesUserPreference), takeUntil(this.destroyed$))
         .subscribe((wrapQueueSwimlanes) => {
            if (wrapQueueSwimlanes == null || this.sessionStorageService.getQueueHistoryToggleState() != null) {
               return;
            }

            this.wrapQueueSwimlanes = wrapQueueSwimlanes;
            this.saveQueueHistoryToggleState({
               queuesWrapSwimlanes: wrapQueueSwimlanes,
               queueCallsWrapSwimlanes: wrapQueueSwimlanes,
            });
         });
   }

   private resetAccountWideQueueViewData(): void {
      this.queueExtensions = [];
      this.allAccountQueueMembers = [];
      this.loggedInQueueMembers = [];
      this.loggedInQueueGroups = [];
      this.queueCallGroups = [];
      this.accountWideQueueHistoryRows = [];
   }

   private startQueueMembersLoading(): number {
      const loadingToken = ++this.nextQueueMembersLoadingToken;
      this.activeQueueMembersLoadingTokens.add(loadingToken);
      this.runQueueMembersViewUpdate(() => {
         this.queueMembersCallState = LoadingState.Loading;
      });
      return loadingToken;
   }

   private finishQueueMembersLoading(loadingToken: number | null | undefined): void {
      if (loadingToken == null) {
         return;
      }

      this.activeQueueMembersLoadingTokens.delete(loadingToken);

      if (this.activeQueueMembersLoadingTokens.size === 0) {
         this.runQueueMembersViewUpdate(() => {
            this.queueMembersCallState = LoadingState.Loaded;
         });
      }
   }

   private resetQueueMembersLoadingState(): void {
      this.pendingAccountQueueLoadToken = null;
      this.activeQueueMembersLoadingTokens.clear();
      this.runQueueMembersViewUpdate(() => {
         this.queueMembersCallState = LoadingState.Loaded;
      });
   }

   private runQueueMembersViewUpdate(updateFn: () => void): void {
      if (this.isDestroyed) {
         return;
      }

      if (NgZone.isInAngularZone()) {
         updateFn();
         this.cdRef.detectChanges();
         return;
      }

      this.ngZone.run(() => {
         if (this.isDestroyed) {
            return;
         }

         updateFn();
         this.cdRef.detectChanges();
      });
   }

   private subscribeToQueueMemberEvents(): void {
      this.actionsSubject
         .pipe(
            filter((action): action is ReturnType<typeof extensionCallQueueIsLoggedInChanged> => {
               return action.type === extensionCallQueueIsLoggedInChanged.type;
            }),
            takeUntil(this.destroyed$)
         )
         .subscribe((action) => {
            this.ngZone.run(() => {
               this.applyQueueMemberLoggedInChange(action);
               this.cdRef.detectChanges();
            });
         });

      this.actionsSubject
         .pipe(
            filter((action): action is ReturnType<typeof extensionCallQueueStatusChanged> => {
               return action.type === extensionCallQueueStatusChanged.type;
            }),
            takeUntil(this.destroyed$)
         )
         .subscribe((action) => {
            this.ngZone.run(() => {
               this.applyQueueMemberStatusChange(action);
               this.cdRef.detectChanges();
            });
         });

      this.actionsSubject
         .pipe(
            filter((action): action is ReturnType<typeof extensionCallQueueCallsChanged> => {
               return action.type === extensionCallQueueCallsChanged.type;
            }),
            takeUntil(this.destroyed$)
         )
         .subscribe((action) => {
            this.ngZone.run(() => {
               this.applyQueueCallChanged(action);
               this.cdRef.detectChanges();
            });
         });

      this.actionsSubject
         .pipe(
            filter((action): action is ReturnType<typeof extensionCallQueueCompleted> => {
               return action.type === extensionCallQueueCompleted.type;
            }),
            takeUntil(this.destroyed$)
         )
         .subscribe((action) => {
            this.ngZone.run(() => {
               this.applyQueueCallCompleted(action);
               this.cdRef.detectChanges();
            });
         });
   }

   private subscribeToHubReconnects(): void {
      this.extensionCallQueueHubService.ndtHubService.reconnected.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.ngZone.run(() => {
            this.resyncSignalrDrivenQueueHistory();
         });
      });
   }

   private resyncSignalrDrivenQueueHistory(): void {
      this.store.dispatch(this.actions.pageEnter());

      if (this.isAccountWide && this.selectedAccountId) {
         this.loadAccountQueueData(false);
      }
   }

   private applyQueueMemberLoggedInChange(action: ReturnType<typeof extensionCallQueueIsLoggedInChanged>): void {
      if (!this.isAccountWide) {
         return;
      }

      let hasChanged = false;
      this.allAccountQueueMembers = this.allAccountQueueMembers.map((queueMember) => {
         if (
            queueMember.queueMemberId !== action.queueMemeberId ||
            queueMember.queueExtensionId !== action.queueExtensionId
         ) {
            return queueMember;
         }

         hasChanged = true;
         return {
            ...queueMember,
            isLoggedIn: action.isLoggedIn,
            agentStatus: action.agentStatus ?? null,
            endWrapUpTime: null,
         };
      });

      if (hasChanged) {
         this.updateLoggedInQueueMembers();
      }
   }

   private applyQueueMemberStatusChange(action: ReturnType<typeof extensionCallQueueStatusChanged>): void {
      if (!this.isAccountWide) {
         return;
      }

      const queueExtensionIds = new Set(action.extensionIds);
      const endWrapUpTime = action.endWrapUpTime ? new Date(action.endWrapUpTime) : null;

      if (!queueExtensionIds.size) {
         return;
      }

      let hasChanged = false;
      this.allAccountQueueMembers = this.allAccountQueueMembers.map((queueMember) => {
         if (
            queueMember.agentExtensionId !== action.agentExtensionId ||
            !queueExtensionIds.has(queueMember.queueExtensionId)
         ) {
            return queueMember;
         }

         hasChanged = true;
         return {
            ...queueMember,
            agentStatus: action.agentStatus ?? null,
            endWrapUpTime,
         };
      });

      if (hasChanged) {
         this.updateLoggedInQueueMembers();
      }
   }

   private applyQueueCallChanged(action: ReturnType<typeof extensionCallQueueCallsChanged>): void {
      if (!this.isAccountWide) {
         return;
      }

      const queueExtensionId = this.toNumber(action.queueExtensionId);
      const queueHistoryId = this.toNumber(action.queueHistoryId);
      const callDispositionId = action.callDispositionId == null ? null : this.toNumber(action.callDispositionId);
      const accountQueueIds = new Set(this.queueExtensions.map((queueExtension) => queueExtension.extensionId));

      if (
         queueExtensionId == null ||
         queueHistoryId == null ||
         !accountQueueIds.has(queueExtensionId) ||
         action.callCompleteDateTime ||
         (callDispositionId != null && callDispositionId !== 5)
      ) {
         this.removeQueueCallFromSnapshot(queueHistoryId ?? action.queueHistoryId);
         return;
      }

      const queueHistoryRow = this.convertQueueCallChangedActionToQueueHistoryRow(action);
      const existingIndex = this.accountWideQueueHistoryRows.findIndex((queueRow) => {
         return queueRow.queueHistoryId === queueHistoryId;
      });

      if (existingIndex >= 0) {
         this.accountWideQueueHistoryRows = this.accountWideQueueHistoryRows.map((queueRow, index) => {
            return index === existingIndex ? queueHistoryRow : queueRow;
         });
      } else {
         this.accountWideQueueHistoryRows = [...this.accountWideQueueHistoryRows, queueHistoryRow];
      }

      this.updateOnPhoneQueueMap(this.accountWideQueueHistoryRows);
      this.updateLoggedInQueueMembers();
   }

   private applyQueueCallCompleted(action: ReturnType<typeof extensionCallQueueCompleted>): void {
      if (!this.isAccountWide) {
         return;
      }

      const queueHistoryId = this.toNumber(action.queueHistoryId);
      if (queueHistoryId == null) {
         return;
      }

      this.removeQueueCallFromSnapshot(queueHistoryId);
   }

   private removeQueueCallFromSnapshot(queueHistoryId: number): void {
      const nextQueueHistoryRows = this.accountWideQueueHistoryRows.filter((queueRow) => {
         return queueRow.queueHistoryId !== queueHistoryId;
      });

      if (nextQueueHistoryRows.length === this.accountWideQueueHistoryRows.length) {
         return;
      }

      this.accountWideQueueHistoryRows = nextQueueHistoryRows;
      this.updateOnPhoneQueueMap(this.accountWideQueueHistoryRows);
      this.updateLoggedInQueueMembers();
   }

   private updateLoggedInQueueMembers(): void {
      const selectedQueueIds = new Set(this.selectedQueueExtensionIds);
      const queueMembersInSelectedQueues = this.allAccountQueueMembers.filter((queueMember) => {
         return selectedQueueIds.has(queueMember.queueExtensionId);
      });

      const selectedAgentGroupKeys = new Set(
         queueMembersInSelectedQueues
            .map((queueMember) => this.getAgentGroupKey(queueMember))
            .filter((agentGroupKey): agentGroupKey is string => !!agentGroupKey)
      );
      const queueMembersForUngrouped = this.allAccountQueueMembers.filter((queueMember) => {
         const agentGroupKey = this.getAgentGroupKey(queueMember);
         return agentGroupKey ? selectedAgentGroupKeys.has(agentGroupKey) : false;
      });

      const loggedInQueueMembers = queueMembersForUngrouped.filter((queueMember) => {
         return queueMember.isLoggedIn;
      });

      const unavailableMembers = this.buildUnavailableAgentMembers(queueMembersForUngrouped);
      this.loggedInQueueMembers = this.sortAgentGroupedMembers([
         ...this.buildAgentGroupedMembers(loggedInQueueMembers),
         ...unavailableMembers,
      ]);
      this.loggedInQueueGroups = this.buildQueueGroupedMembers(queueMembersInSelectedQueues);
      this.queueCallGroups = this.buildQueueCallGroups(this.accountWideQueueHistoryRows);
   }

   private buildAgentGroupedMembers(queueMembers: LoggedInQueueMemberStatus[]): LoggedInQueueMemberDisplay[] {
      const groupedMembers = new Map<string, LoggedInQueueMemberDisplay>();

      queueMembers.forEach((queueMember) => {
         const groupKey = this.getQueueMemberGroupKey(queueMember);
         const existingMember = groupedMembers.get(groupKey);

         if (!existingMember) {
            groupedMembers.set(groupKey, {
               agentExtensionId: queueMember.agentExtensionId,
               agentName: queueMember.agentName,
               agentNumber: queueMember.agentNumber,
               agentStatus: queueMember.agentStatus,
               endWrapUpTime: queueMember.endWrapUpTime,
               queues: [
                  {
                     queueExtensionId: queueMember.queueExtensionId,
                     queueName: queueMember.queueName,
                     queueNumber: queueMember.queueNumber,
                     isOnThePhone: this.isOnThePhoneInQueue(queueMember),
                     isRinging: this.isRingingInQueue(queueMember),
                  },
               ],
            });
            return;
         }

         const existingQueue = existingMember.queues.find(
            (queue) => queue.queueExtensionId === queueMember.queueExtensionId
         );
         if (!existingQueue) {
            existingMember.queues.push({
               queueExtensionId: queueMember.queueExtensionId,
               queueName: queueMember.queueName,
               queueNumber: queueMember.queueNumber,
               isOnThePhone: this.isOnThePhoneInQueue(queueMember),
               isRinging: this.isRingingInQueue(queueMember),
            });
         } else {
            existingQueue.isOnThePhone = existingQueue.isOnThePhone || this.isOnThePhoneInQueue(queueMember);
            existingQueue.isRinging = existingQueue.isRinging || this.isRingingInQueue(queueMember);
         }

         if (!existingMember.agentStatus && queueMember.agentStatus) {
            existingMember.agentStatus = queueMember.agentStatus;
         }

         if (!existingMember.endWrapUpTime && queueMember.endWrapUpTime) {
            existingMember.endWrapUpTime = queueMember.endWrapUpTime;
         }
      });

      return this.sortAgentGroupedMembers(
         Array.from(groupedMembers.values()).map((member) => {
            member.queues.sort((a, b) => {
               const queueNameA = `${a.queueNumber ?? ''} ${a.queueName ?? ''}`.trim().toLocaleLowerCase();
               const queueNameB = `${b.queueNumber ?? ''} ${b.queueName ?? ''}`.trim().toLocaleLowerCase();
               return queueNameA.localeCompare(queueNameB);
            });
            return member;
         })
      );
   }

   private buildUnavailableAgentMembers(queueMembers: LoggedInQueueMemberStatus[]): LoggedInQueueMemberDisplay[] {
      const queueMembersByAgent = new Map<string, LoggedInQueueMemberStatus[]>();

      queueMembers.forEach((queueMember) => {
         const agentGroupKey = this.getAgentGroupKey(queueMember);
         if (!agentGroupKey) {
            return;
         }

         const existingQueueMembers = queueMembersByAgent.get(agentGroupKey) ?? [];
         existingQueueMembers.push(queueMember);
         queueMembersByAgent.set(agentGroupKey, existingQueueMembers);
      });

      const unavailableMembers: LoggedInQueueMemberDisplay[] = [];
      queueMembersByAgent.forEach((membersByAgent) => {
         if (membersByAgent.some((member) => member.isLoggedIn)) {
            return;
         }

         const firstQueueMember = membersByAgent[0];
         unavailableMembers.push({
            agentExtensionId: firstQueueMember.agentExtensionId,
            agentName: firstQueueMember.agentName,
            agentNumber: firstQueueMember.agentNumber,
            agentStatus: 'Unavailable',
            endWrapUpTime: null,
            queues: [],
         });
      });

      return unavailableMembers;
   }

   private buildQueueGroupedMembers(queueMembers: LoggedInQueueMemberStatus[]): LoggedInQueueDisplay[] {
      const groupedQueues = new Map<number, LoggedInQueueDisplay>();
      const queueAgentKeys = new Map<number, Set<string>>();

      queueMembers.forEach((queueMember) => {
         const isOnThePhone = this.isOnThePhoneInQueue(queueMember);
         const isRinging = this.isRingingInQueue(queueMember);
         const activeCallQueue = this.getOtherActiveCallQueueReference(queueMember);
         const activeRingingQueue = this.getOtherRingingQueueReference(queueMember);
         const activeQueueCall = this.getActiveQueueCallForAgent(queueMember);
         const queueMemberDisplayStatus = this.getQueueMemberDisplayStatus(queueMember);
         const queueMemberDisplayWrapUpTime = this.getQueueMemberDisplayWrapUpTime(queueMember);
         let groupedQueue = groupedQueues.get(queueMember.queueExtensionId);

         if (!groupedQueue) {
            groupedQueue = {
               queueExtensionId: queueMember.queueExtensionId,
               queueName: queueMember.queueName,
               queueNumber: queueMember.queueNumber,
               agents: [],
            };
            groupedQueues.set(queueMember.queueExtensionId, groupedQueue);
            queueAgentKeys.set(queueMember.queueExtensionId, new Set<string>());
         }

         const agentKey = this.getQueueMemberGroupKey(queueMember);
         const existingAgentKeys = queueAgentKeys.get(queueMember.queueExtensionId);
         if (!existingAgentKeys) {
            return;
         }

         if (!existingAgentKeys.has(agentKey)) {
            groupedQueue.agents.push({
               agentExtensionId: queueMember.agentExtensionId,
               agentName: queueMember.agentName,
               agentNumber: queueMember.agentNumber,
               agentStatus: queueMemberDisplayStatus,
               endWrapUpTime: queueMemberDisplayWrapUpTime,
               isOnThePhone,
               isRinging,
               activeCallQueue,
               activeRingingQueue,
               activeQueueCall,
            });
            existingAgentKeys.add(agentKey);
            return;
         }

         const existingAgent = groupedQueue.agents.find((agent) => {
            if (queueMember.agentExtensionId != null) {
               return agent.agentExtensionId === queueMember.agentExtensionId;
            }

            return (
               agent.agentExtensionId == null &&
               agent.agentName === queueMember.agentName &&
               agent.agentNumber === queueMember.agentNumber
            );
         });

         if (!existingAgent) {
            return;
         }

         existingAgent.isOnThePhone = existingAgent.isOnThePhone || isOnThePhone;
         existingAgent.isRinging = existingAgent.isRinging || isRinging;
         if (!existingAgent.activeCallQueue && activeCallQueue) {
            existingAgent.activeCallQueue = activeCallQueue;
         }
         if (!existingAgent.activeRingingQueue && activeRingingQueue) {
            existingAgent.activeRingingQueue = activeRingingQueue;
         }
         existingAgent.activeQueueCall = this.getPreferredQueueAgentActiveQueueCall(
            existingAgent.activeQueueCall,
            activeQueueCall
         );
         if (queueMember.isLoggedIn) {
            existingAgent.agentStatus = queueMemberDisplayStatus ?? existingAgent.agentStatus;
         } else if (!existingAgent.agentStatus) {
            existingAgent.agentStatus = queueMemberDisplayStatus;
         }
         if (queueMember.isLoggedIn && !existingAgent.endWrapUpTime && queueMemberDisplayWrapUpTime) {
            existingAgent.endWrapUpTime = queueMemberDisplayWrapUpTime;
         }
      });

      return Array.from(groupedQueues.values())
         .map((queue) => {
            queue.agents.sort((a, b) => {
               const agentNameA = (a.agentName ?? '').toLocaleLowerCase();
               const agentNameB = (b.agentName ?? '').toLocaleLowerCase();
               if (agentNameA !== agentNameB) {
                  return agentNameA.localeCompare(agentNameB);
               }

               const agentNumberA = (a.agentNumber ?? '').toLocaleLowerCase();
               const agentNumberB = (b.agentNumber ?? '').toLocaleLowerCase();
               return agentNumberA.localeCompare(agentNumberB);
            });

            return queue;
         })
         .sort((a, b) => {
            const queueNameA = `${a.queueNumber ?? ''} ${a.queueName ?? ''}`.trim().toLocaleLowerCase();
            const queueNameB = `${b.queueNumber ?? ''} ${b.queueName ?? ''}`.trim().toLocaleLowerCase();
            return queueNameA.localeCompare(queueNameB);
         });
   }

   private buildQueueCallGroups(queueRows: QueueHistoryRegisterListItem[]): LoggedInQueueCallDisplay[] {
      const selectedQueueIds = new Set(this.selectedQueueExtensionIds);
      const groupedQueues = new Map<number, LoggedInQueueCallDisplay>();

      queueRows.forEach((queueRow) => {
         const queueExtensionId = this.toNumber(queueRow.queueExtensionId);
         if (queueExtensionId == null || !selectedQueueIds.has(queueExtensionId)) {
            return;
         }

         const queueReference = this.getQueueReference(queueRow, queueExtensionId);
         let groupedQueue = groupedQueues.get(queueExtensionId);

         if (!groupedQueue) {
            groupedQueue = {
               ...queueReference,
               activeCalls: [],
            };
            groupedQueues.set(queueExtensionId, groupedQueue);
         }

         groupedQueue.activeCalls.push(queueRow);
      });

      return Array.from(groupedQueues.values())
         .map((queue) => {
            queue.activeCalls.sort((a, b) => {
               return this.compareQueueCallsForQueueCard(a, b);
            });
            return queue;
         })
         .sort((a, b) => {
            const queueNameA = `${a.queueNumber ?? ''} ${a.queueName ?? ''}`.trim().toLocaleLowerCase();
            const queueNameB = `${b.queueNumber ?? ''} ${b.queueName ?? ''}`.trim().toLocaleLowerCase();
            return queueNameA.localeCompare(queueNameB);
         });
   }

   private getQueueReference(
      queueRow: QueueHistoryRegisterListItem,
      queueExtensionId: number
   ): Omit<LoggedInQueueCallDisplay, 'activeCalls'> {
      const directQueue = queueRow.queueExtension;
      if (directQueue) {
         return {
            queueExtensionId,
            queueName: this.getQueueDisplayName(directQueue.shortName, directQueue.name),
            queueNumber: this.toNumber(directQueue.number),
         };
      }

      const queueExtension = this.queueExtensions.find((item) => item.extensionId === queueExtensionId);
      return {
         queueExtensionId,
         queueName: this.getQueueDisplayName(queueExtension?.shortName, queueExtension?.name),
         queueNumber: queueExtension?.number ?? null,
      };
   }

   private async refreshAccountWideQueueHistorySnapshot(loadingToken: number | null = null): Promise<void> {
      try {
         if (!this.isAccountWide) {
            return;
         }

         const isLoggedIn = await firstValueFrom(this.store.pipe(select(getIsLoggedIn)));
         if (!isLoggedIn) {
            return;
         }

         const selectedQueueExtensionIds = [...this.selectedQueueExtensionIds];
         const requestId = ++this.queueHistorySnapshotRequestId;

         if (!selectedQueueExtensionIds.length) {
            this.runQueueMembersViewUpdate(() => {
               this.accountWideQueueHistoryRows = [];
               this.updateOnPhoneQueueMap(this.accountWideQueueHistoryRows);
               this.updateLoggedInQueueMembers();
            });
            return;
         }

         const queueExtensionIds = this.queueExtensions.length
            ? this.queueExtensions.map((queueExtension) => queueExtension.extensionId)
            : selectedQueueExtensionIds;
         const queueHistoryRows: QueueHistoryRegisterListItem[] = [];
         let endCursor: string | null = null;
         let hasNextPage = true;

         while (hasNextPage) {
            const result = await firstValueFrom(
               this.queueHistoryRegisterApiService.getQueueHistory(
                  endCursor,
                  this.buildQueueCallSnapshotFilters(queueExtensionIds),
                  null,
                  this.accountWideOrderingOptions
               )
            );

            if (requestId !== this.queueHistorySnapshotRequestId || !this.isAccountWide || this.isDestroyed) {
               return;
            }

            const queueHistoryConnection = result.data?.queueHistoriesConnection;
            if (!queueHistoryConnection) {
               break;
            }

            queueHistoryRows.push(
               ...queueHistoryConnection.items.map((queueHistoryItem) => {
                  return this.convertQueueHistorySnapshotItem(queueHistoryItem);
               })
            );

            hasNextPage = queueHistoryConnection.pageInfo.hasNextPage;
            endCursor = queueHistoryConnection.pageInfo.endCursor;
         }

         if (requestId !== this.queueHistorySnapshotRequestId || !this.isAccountWide || this.isDestroyed) {
            return;
         }

         this.runQueueMembersViewUpdate(() => {
            this.accountWideQueueHistoryRows = queueHistoryRows;
            this.updateOnPhoneQueueMap(this.accountWideQueueHistoryRows);
            this.updateLoggedInQueueMembers();
         });
      } catch {
         return;
      } finally {
         this.finishQueueMembersLoading(loadingToken);

         if (loadingToken != null && this.pendingAccountQueueLoadToken === loadingToken) {
            this.pendingAccountQueueLoadToken = null;
         }
      }
   }

   private buildQueueCallSnapshotFilters(queueExtensionIds: number[]): QueueHistoryFilterOptions[] {
      const filters: QueueHistoryFilterOptions[] = [
         {
            field: 'CallDispositionId',
            operation: FilterOperations.EqualToOrNull,
            type: 'numeric',
            value: 5,
         },
         {
            field: 'CallCompleteDateTime',
            operation: FilterOperations.HasValue,
            type: 'boolean',
            value: false,
         },
      ];

      if (queueExtensionIds.length) {
         filters.push({
            field: 'QueueExtensionId',
            operation: FilterOperations.In,
            type: 'numeric',
            value: queueExtensionIds,
         } as QueueHistoryFilterOptions);
      }

      return filters;
   }

   private convertQueueHistorySnapshotItem(
      queueHistoryItem: GetQueueHistory_queueHistoriesConnection_items
   ): QueueHistoryRegisterListItem {
      return {
         ...queueHistoryItem,
         queueExtensionId: queueHistoryItem.queueExtensionId,
         queueExtension: queueHistoryItem.queueExtension
            ? {
                 extensionId: queueHistoryItem.queueExtension.extensionId,
                 name: queueHistoryItem.queueExtension.name ?? null,
                 shortName: queueHistoryItem.queueExtension.shortName ?? null,
                 number: queueHistoryItem.queueExtension.number ?? null,
              }
            : undefined,
         callDetailId: queueHistoryItem.activeCall?.callDetailId ?? queueHistoryItem.callDetail?.callDetailId ?? null,
         callerChannelId:
            queueHistoryItem.activeCall?.callerChannelId ?? queueHistoryItem.callDetail?.callerChannelId ?? null,
         serverName: queueHistoryItem.activeCall?.serverName ?? queueHistoryItem.callDetail?.serverName ?? null,
         sipCallId: queueHistoryItem.activeCall?.sipCallId ?? queueHistoryItem.callDetail?.sipCallId ?? null,
         hasPreventEmptyQueue:
            queueHistoryItem.activeCall?.destinationExtension?.extensionQueue?.hasPreventEmptyQueue ??
            queueHistoryItem.callDetail?.destinationExtension?.extensionQueue?.hasPreventEmptyQueue ??
            false,
         callerIdNumber:
            queueHistoryItem.callerIdNumber?.startsWith('1') && queueHistoryItem.callerIdNumber.length > 4
               ? queueHistoryItem.callerIdNumber.substring(1)
               : queueHistoryItem.callerIdNumber,
         agentExtension: queueHistoryItem.agentExtension
            ? {
                 extensionId: queueHistoryItem.agentExtension.extensionId,
                 name: queueHistoryItem.agentExtension.name ?? null,
                 number: queueHistoryItem.agentExtension.number ?? null,
              }
            : undefined,
      };
   }

   private convertQueueCallChangedActionToQueueHistoryRow(
      action: ReturnType<typeof extensionCallQueueCallsChanged>
   ): QueueHistoryRegisterListItem {
      return {
         queueHistoryId: action.queueHistoryId,
         queueExtensionId: action.queueExtensionId,
         queueExtension: action.queueExtension ?? undefined,
         callDetailId: action.callDetailId ?? null,
         callerChannelId: action.callerChannelId ?? null,
         serverName: action.serverName ?? null,
         sipCallId: action.sipCallId ?? null,
         hasPreventEmptyQueue: !!action.hasPreventEmptyQueue,
         callerIdName: action.callerIdName ?? null,
         callerIdNumber:
            action.callerIdNumber?.startsWith('1') && action.callerIdNumber.length > 4
               ? action.callerIdNumber.substring(1)
               : action.callerIdNumber ?? null,
         callerStatus: action.callerStatus,
         isCallbackRequested: !!action.isCallBackRequested,
         enqueuedDateTime: action.enqueuedDateTime ?? new Date(),
         answeredDateTime: action.answeredDateTime ?? null,
         callbackAttempts: action.callbackAttempts ?? 0,
         waitTime: action.waitTime ?? 0,
         agentExtension: action.agentExtension
            ? {
                 extensionId: action.agentExtension.extensionId,
                 name: action.agentExtension.name ?? null,
                 number: action.agentExtension.number ?? null,
              }
            : undefined,
      };
   }

   private compareQueueCallsForQueueCard(
      leftQueueCall: QueueHistoryRegisterListItem,
      rightQueueCall: QueueHistoryRegisterListItem
   ): number {
      const leftIsConnected = this.isConnectedCallerStatus(leftQueueCall.callerStatus);
      const rightIsConnected = this.isConnectedCallerStatus(rightQueueCall.callerStatus);

      if (leftIsConnected !== rightIsConnected) {
         return leftIsConnected ? -1 : 1;
      }

      const leftEnqueuedDateTime = this.toDateValue(leftQueueCall.enqueuedDateTime);
      const rightEnqueuedDateTime = this.toDateValue(rightQueueCall.enqueuedDateTime);

      if (leftEnqueuedDateTime == null || rightEnqueuedDateTime == null) {
         if (leftEnqueuedDateTime == null && rightEnqueuedDateTime == null) {
            return leftQueueCall.queueHistoryId - rightQueueCall.queueHistoryId;
         }

         return leftEnqueuedDateTime == null ? 1 : -1;
      }

      if (leftEnqueuedDateTime !== rightEnqueuedDateTime) {
         return leftEnqueuedDateTime - rightEnqueuedDateTime;
      }

      return leftQueueCall.queueHistoryId - rightQueueCall.queueHistoryId;
   }

   private updateOnPhoneQueueMap(queueRows: QueueHistoryRegisterListItem[]): void {
      const onPhoneMap = new Map<number, Set<number>>();
      const ringingMap = new Map<number, Set<number>>();
      const activeQueueCallMap = new Map<number, QueueHistoryRegisterListItem>();

      queueRows.forEach((queueRow) => {
         const queueExtensionId = queueRow.queueExtensionId ? Number(queueRow.queueExtensionId) : null;
         if (queueExtensionId == null) {
            return;
         }

         const agentExtensionId = queueRow.agentExtension?.extensionId
            ? Number(queueRow.agentExtension.extensionId)
            : null;
         if (agentExtensionId == null) {
            return;
         }

         if (this.isConnectedCallerStatus(queueRow.callerStatus)) {
            const queueIds = onPhoneMap.get(agentExtensionId) ?? new Set<number>();
            queueIds.add(queueExtensionId);
            onPhoneMap.set(agentExtensionId, queueIds);
            activeQueueCallMap.set(
               agentExtensionId,
               this.getPreferredQueueAgentActiveQueueCall(activeQueueCallMap.get(agentExtensionId) ?? null, queueRow) ??
                  queueRow
            );
         }

         if (this.isRingingCallerStatus(queueRow.callerStatus)) {
            const queueIds = ringingMap.get(agentExtensionId) ?? new Set<number>();
            queueIds.add(queueExtensionId);
            ringingMap.set(agentExtensionId, queueIds);
            activeQueueCallMap.set(
               agentExtensionId,
               this.getPreferredQueueAgentActiveQueueCall(activeQueueCallMap.get(agentExtensionId) ?? null, queueRow) ??
                  queueRow
            );
         }
      });

      this.onPhoneQueueByAgentId = onPhoneMap;
      this.ringingQueueByAgentId = ringingMap;
      this.activeQueueCallByAgentId = activeQueueCallMap;
   }

   private isOnThePhoneInQueue(queueMember: LoggedInQueueMemberStatus): boolean {
      if (!queueMember.isLoggedIn) {
         return false;
      }

      if (queueMember.agentExtensionId == null) {
         return false;
      }

      // Keep queue-pill phone icons strict to queue row evidence.
      // Status events can mark an agent as OnThePhone without reliably identifying the exact queue.
      return this.onPhoneQueueByAgentId.get(queueMember.agentExtensionId)?.has(queueMember.queueExtensionId) ?? false;
   }

   private isRingingInQueue(queueMember: LoggedInQueueMemberStatus): boolean {
      if (!queueMember.isLoggedIn) {
         return false;
      }

      if (queueMember.agentExtensionId == null) {
         return false;
      }

      return this.ringingQueueByAgentId.get(queueMember.agentExtensionId)?.has(queueMember.queueExtensionId) ?? false;
   }

   private getOtherActiveCallQueueReference(queueMember: LoggedInQueueMemberStatus): LoggedInQueueReference | null {
      return this.getOtherQueueReference(queueMember, this.onPhoneQueueByAgentId);
   }

   private getOtherRingingQueueReference(queueMember: LoggedInQueueMemberStatus): LoggedInQueueReference | null {
      return this.getOtherQueueReference(queueMember, this.ringingQueueByAgentId);
   }

   private getActiveQueueCallForAgent(queueMember: LoggedInQueueMemberStatus): QueueHistoryRegisterListItem | null {
      if (!queueMember.isLoggedIn || queueMember.agentExtensionId == null) {
         return null;
      }

      return this.activeQueueCallByAgentId.get(queueMember.agentExtensionId) ?? null;
   }

   private getPreferredQueueAgentActiveQueueCall(
      existingQueueCall: QueueHistoryRegisterListItem | null,
      nextQueueCall: QueueHistoryRegisterListItem | null
   ): QueueHistoryRegisterListItem | null {
      if (!existingQueueCall) {
         return nextQueueCall;
      }

      if (!nextQueueCall) {
         return existingQueueCall;
      }

      const existingIsConnected = this.isConnectedCallerStatus(existingQueueCall.callerStatus);
      const nextIsConnected = this.isConnectedCallerStatus(nextQueueCall.callerStatus);

      if (existingIsConnected !== nextIsConnected) {
         return nextIsConnected ? nextQueueCall : existingQueueCall;
      }

      return this.compareQueueCallsForQueueCard(nextQueueCall, existingQueueCall) < 0
         ? nextQueueCall
         : existingQueueCall;
   }

   private getOtherQueueReference(
      queueMember: LoggedInQueueMemberStatus,
      queueIdsByAgentId: Map<number, Set<number>>
   ): LoggedInQueueReference | null {
      if (!queueMember.isLoggedIn || queueMember.agentExtensionId == null) {
         return null;
      }

      const activeQueueIds = queueIdsByAgentId.get(queueMember.agentExtensionId);
      if (!activeQueueIds?.size) {
         return null;
      }

      const otherActiveQueues = Array.from(activeQueueIds)
         .filter((queueExtensionId) => queueExtensionId !== queueMember.queueExtensionId)
         .map((queueExtensionId) => this.getQueueReferenceById(queueExtensionId))
         .filter((queueReference): queueReference is LoggedInQueueReference => queueReference != null)
         .sort((leftQueue, rightQueue) => this.compareQueueReferencesForDisplay(leftQueue, rightQueue));

      return otherActiveQueues[0] ?? null;
   }

   private getQueueReferenceById(queueExtensionId: number): LoggedInQueueReference | null {
      const queueMember = this.allAccountQueueMembers.find((accountQueueMember) => {
         return accountQueueMember.queueExtensionId === queueExtensionId;
      });
      if (queueMember) {
         return {
            queueExtensionId,
            queueName: queueMember.queueName,
            queueNumber: queueMember.queueNumber,
         };
      }

      const queueExtension = this.queueExtensions.find((accountQueueExtension) => {
         return accountQueueExtension.extensionId === queueExtensionId;
      });
      if (queueExtension) {
         return {
            queueExtensionId,
            queueName: this.getQueueDisplayName(queueExtension.shortName, queueExtension.name),
            queueNumber: queueExtension.number ?? null,
         };
      }

      return null;
   }

   private getQueueDisplayName(shortName: string | null | undefined, name: string | null | undefined): string | null {
      return shortName?.trim() || name?.trim() || null;
   }

   private compareQueueReferencesForDisplay(
      leftQueue: LoggedInQueueReference,
      rightQueue: LoggedInQueueReference
   ): number {
      const leftQueueDisplay = `${leftQueue.queueNumber ?? ''} ${leftQueue.queueName ?? ''}`.trim().toLocaleLowerCase();
      const rightQueueDisplay = `${rightQueue.queueNumber ?? ''} ${rightQueue.queueName ?? ''}`
         .trim()
         .toLocaleLowerCase();

      return leftQueueDisplay.localeCompare(rightQueueDisplay);
   }

   private getQueueMemberDisplayStatus(queueMember: LoggedInQueueMemberStatus): string | null {
      return queueMember.isLoggedIn ? queueMember.agentStatus ?? null : 'Unavailable';
   }

   private getQueueMemberDisplayWrapUpTime(queueMember: LoggedInQueueMemberStatus): Date | null {
      return queueMember.isLoggedIn ? queueMember.endWrapUpTime : null;
   }

   private resetOnPhoneQueueMaps(): void {
      this.onPhoneQueueByAgentId = new Map<number, Set<number>>();
      this.ringingQueueByAgentId = new Map<number, Set<number>>();
      this.activeQueueCallByAgentId = new Map<number, QueueHistoryRegisterListItem>();
   }

   private getQueueMemberGroupKey(queueMember: LoggedInQueueMemberStatus): string {
      return queueMember.agentExtensionId != null
         ? `agent-${queueMember.agentExtensionId}`
         : `member-${queueMember.queueMemberId}`;
   }

   private getAgentGroupKey(queueMember: LoggedInQueueMemberStatus): string | null {
      if (queueMember.agentExtensionId != null) {
         return `agent-${queueMember.agentExtensionId}`;
      }

      const name = queueMember.agentName?.trim().toLocaleLowerCase() ?? '';
      const number = queueMember.agentNumber?.trim().toLocaleLowerCase() ?? '';
      if (!name && !number) {
         return null;
      }

      return `agent-fallback-${name}-${number}`;
   }

   private sortAgentGroupedMembers(queueMembers: LoggedInQueueMemberDisplay[]): LoggedInQueueMemberDisplay[] {
      return queueMembers.sort((a, b) => {
         const agentNameA = (a.agentName ?? '').toLocaleLowerCase();
         const agentNameB = (b.agentName ?? '').toLocaleLowerCase();
         if (agentNameA !== agentNameB) {
            return agentNameA.localeCompare(agentNameB);
         }

         const agentNumberA = (a.agentNumber ?? '').toLocaleLowerCase();
         const agentNumberB = (b.agentNumber ?? '').toLocaleLowerCase();
         return agentNumberA.localeCompare(agentNumberB);
      });
   }

   private isConnectedCallerStatus(callerStatus: QueueCallerStatus | string | null | undefined): boolean {
      if (callerStatus == null) {
         return false;
      }

      if (callerStatus === QueueCallerStatus.Connected) {
         return true;
      }

      return String(callerStatus).toLocaleLowerCase() === QueueCallerStatus.Connected.toLocaleLowerCase();
   }

   private isRingingCallerStatus(callerStatus: QueueCallerStatus | string | null | undefined): boolean {
      if (callerStatus == null) {
         return false;
      }

      if (callerStatus === QueueCallerStatus.DialingAnAgent) {
         return true;
      }

      return String(callerStatus).toLocaleLowerCase() === QueueCallerStatus.DialingAnAgent.toLocaleLowerCase();
   }

   private toNumber(value: unknown): number | null {
      const parsedNumber = Number(value);
      return Number.isFinite(parsedNumber) ? parsedNumber : null;
   }

   private toDateValue(value: Date | string | number | null | undefined): number | null {
      if (value == null) {
         return null;
      }

      if (value instanceof Date) {
         return value.getTime();
      }

      const timestamp = new Date(value).getTime();
      return Number.isFinite(timestamp) ? timestamp : null;
   }

   private dispatchInitialFilters(
      orderingOptions: QueueHistoryOrderingOptions[] | undefined,
      queueExtensionId: number | null
   ): void {
      this.store.dispatch(
         queueHistoryRegisterInitialFilter({
            filters: this.filters,
            queueExtensionId,
            orderingOptions,
         })
      );
      this.store.dispatch(this.actions.pageEnter());
   }

   private startAccountQueueRefresh(): void {
      this.stopAccountQueueRefresh();

      this.accountQueueRefreshTimer = setInterval(() => {
         firstValueFrom(this.store.pipe(select(getIsLoggedIn))).then((isLoggedIn) => {
            if (!isLoggedIn) {
               this.stopAccountQueueRefresh();
               return;
            }
            this.loadAccountQueueData(false);
         });
      }, 30000);
   }

   private stopAccountQueueRefresh(): void {
      if (this.accountQueueRefreshTimer) {
         clearInterval(this.accountQueueRefreshTimer);
      }
      this.accountQueueRefreshTimer = null;
   }

   private buildFilters(): QueueHistoryFilterOptions[] {
      const filters: QueueHistoryFilterOptions[] = [
         {
            field: 'CallDispositionId',
            operation: FilterOperations.EqualToOrNull,
            type: 'numeric',
            value: 5,
         },
         {
            field: 'CallCompleteDateTime',
            operation: FilterOperations.HasValue,
            type: 'boolean',
            value: false,
         },
      ];

      if (this.isAccountWide) {
         if (this.selectedQueueExtensionIds.length > 0) {
            filters.push({
               field: 'QueueExtensionId',
               operation: FilterOperations.In,
               type: 'numeric',
               value: this.selectedQueueExtensionIds,
            } as QueueHistoryFilterOptions);
         } else {
            filters.push({
               field: 'QueueExtensionId',
               operation: FilterOperations.EqualTo,
               type: 'numeric',
               value: 0,
            } as QueueHistoryFilterOptions);
         }
      } else {
         filters.push({
            field: 'QueueExtensionId',
            operation: FilterOperations.EqualTo,
            type: 'numeric',
            value: this.extensionId ?? 0,
         } as QueueHistoryFilterOptions);
      }

      return filters;
   }

   isQueueMembersTabIndex(tabIndex: number): boolean {
      return (
         tabIndex === this.agentStatusTabIndex ||
         tabIndex === this.queueStatusTabIndex ||
         tabIndex === this.queueCallsTabIndex
      );
   }

   private triggerActiveQueueCallsTableReflow(): void {
      if (typeof window === 'undefined') {
         return;
      }

      requestAnimationFrame(() => {
         requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'));
            this.cdRef.detectChanges();
         });
      });
   }

   private getSelectedAccountWideTabIndexFromRoute(): number {
      const tabName = this.route.snapshot.params['tab'];
      if (!tabName) {
         return this.queueCallsTabIndex;
      }

      const tabIndex = this.accountWideTabRouteNames.findIndex(
         (routeName) => routeName === tabName.toLocaleLowerCase()
      );
      return tabIndex >= 0 ? tabIndex : this.queueCallsTabIndex;
   }
}
